import eyeIcon from '../../../assets/svg/eye-primary.svg';
import Datatable from '@/components/common/Datatable';
import { useGetAllOrder } from '@/services/OrderManagement';
import {
  Box,
  Flex,
  Icon,
  Text,
  Tooltip,
  Modal,
  Image,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalHeader,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { convertDateFormat } from '../../../utils/dateConverter';
import StatusBadge from '@/components/common/StatusBadge';
import OrderDetails from '../OrderDetail';
import { Column } from 'react-table';
import { Order } from '@/services/OrderManagement/types';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import CustomizedIcon from '../../../assets/svg/customizable-icon.svg?react';
import CustomizationDetailsModal from '@/components/common/CustomizationDetailsModal';

interface FilterState {
  start_date?: string;
  end_date?: string;
  category?: string;
  subcategory?: string;
  assign_title?: string;
  user_status?: boolean | null;
}

interface FailedDeliveryProps {
  filters?: FilterState;
  setFilters?: React.Dispatch<React.SetStateAction<FilterState>>;
}

const FailedDelivery = ({
  filters: parentFilters,
  setFilters: setParentFilters,
}: FailedDeliveryProps) => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const [query, setQuery] = useState<string>('');

  const [localFilters, setLocalFilters] = useState<FilterState>(
    parentFilters || {
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
      assign_title: '',
      user_status: null,
    },
  );

  useEffect(() => {
    if (parentFilters) {
      setLocalFilters(parentFilters);
    }
  }, [parentFilters]);

  const effectiveFilters = parentFilters ?? localFilters;

  const handleSetFilters = (newFilters: FilterState | ((prev: FilterState) => FilterState)) => {
    setLocalFilters(newFilters);
    if (setParentFilters) {
      setParentFilters(newFilters);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);

  const {
    data: responseData,
    isLoading,
    isError,
  } = useGetAllOrder({
    status: 'Delivery Failed',
    q: query,
    page: pagination.page,
    page_size: pagination.pageSize,
    ...effectiveFilters,
  });

  const isArrayResponse = Array.isArray(responseData);
  const orders: Order[] = isArrayResponse ? responseData : responseData?.results ?? [];

  const totalCount = isArrayResponse ? orders.length : responseData?.count ?? 0;

  const totalPages = isArrayResponse ? 1 : responseData?.page_count ?? 1;

  const currentPage = isArrayResponse ? 1 : responseData?.active_page ?? pagination.page;

  useEffect(() => {
    if (responseData) {
      setPagination((prev) => ({
        ...prev,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    }
  }, [responseData, totalCount, totalPages, currentPage]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    const resetState = {
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
      assign_title: '',
      user_status: null,
    };
    setLocalFilters(resetState);
    if (setParentFilters) {
      setParentFilters(resetState);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const openCustomizationModal = (order: Order) => {
    setSelectedOrder(order);
    setIsCustomizationModalOpen(true);
  };

  const columns = useMemo<Column<Order>[]>(
    () => [
      {
        Header: 'Order ID',
        accessor: 'order_id',
        Cell: ({ value, row }: { value: string; row: { original: Order } }) => {
          const isCustomized = row.original.is_customized;
          return (
            <Flex align="center" gap={2}>
              <Text>{value ?? '-'}</Text>
              {isCustomized && (
                <Tooltip label="This product is customized" placement="bottom" hasArrow>
                  <Box
                    bg={'#F2F4F8'}
                    borderRadius={'3px'}
                    p={1}
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCustomizationModal(row.original);
                    }}
                    _hover={{ bg: 'brand.primary.50' }}
                  >
                    <Icon as={CustomizedIcon} color={'brand.primary.DEFAULT'} boxSize={4} />
                  </Box>
                </Tooltip>
              )}
            </Flex>
          );
        },
      },
      {
        Header: 'Product Name',
        accessor: 'product',
        Cell: ({ value }: { value: string }) => <Text>{value ?? '-'}</Text>,
      },
      {
        Header: 'Ship To',
        accessor: (row) => row.shipping_address?.name ?? '-',
        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: 'Status',
        accessor: (row) => row.order_status?.status_name ?? '-',
        Cell: ({ value }: { value: string }) => <StatusBadge status={value} />,
      },
      {
        Header: 'Total Price',
        accessor: 'total_amount',
        Cell: ({ value, row }: { value: string; row: { original: Order } }) => {
          const price = value ? parseFloat(value) : null;
          const currency = row.original.currency ?? 'USD';
          return (
            <Text>
              {price !== null ? `${getCurrencySymbol(currency)}${price.toFixed(2)}` : '-'}
            </Text>
          );
        },
      },
      {
        Header: 'Payment',
        accessor: (row) => row.payment_method?.method_name ?? '-',
        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: 'Order Placed',
        accessor: 'created_at',
        Cell: ({ value }: { value: string }) => <Text>{convertDateFormat(value ?? '')}</Text>,
      },
      {
        Header: 'Action',
        id: 'action',
        Cell: ({ row }: { row: { original: Order } }) => (
          <Flex gap={2} align="center" minWidth="80px">
            <Tooltip label="View Order" placement="bottom" hasArrow>
              <Image
                src={eyeIcon}
                boxSize={5}
                color="brand.primary.DEFAULT"
                onClick={() => handleViewOrder(row.original.order_id)}
                cursor="pointer"
              />
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [],
  );

  return (
    <Box bg="background.white">
      <Box width="auto" overflow="auto">
        <Datatable<Order>
          data={orders}
          isLoading={isLoading}
          isError={isError}
          columns={columns}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalCount: pagination.totalCount,
            totalPages: pagination.totalPages,
          }}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          filters={effectiveFilters}
          setFilters={handleSetFilters}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          sticky={true}
        />
      </Box>

      {/* Order Details Modal */}
      <Modal size="5xl" isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Order Details</ModalHeader>
          <ModalBody
            maxHeight="70vh"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
            }}
          >
            {selectedOrderId && <OrderDetails id={selectedOrderId} />}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand.primary" onClick={closeModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Customization Details Modal */}
      <CustomizationDetailsModal
        isOpen={isCustomizationModalOpen}
        onClose={() => setIsCustomizationModalOpen(false)}
        order={selectedOrder ?? undefined}
      />
    </Box>
  );
};

export default FailedDelivery;
